import CryptoJS from 'crypto-js';
import config from 'config/config';

const ENCRYPTION_KEY = config.ENCRYPTION_KEY;
const IV_LENGTH = 16;

/**
 * AES256 암호화
 * @param plainText 암호화할 평문
 * @returns 암호화된 문자열 (Base64)
 */
export const encrypt = (plainText: string): string => {
    if (!plainText) return '';

    try {
        // 랜덤 IV 생성
        const iv = CryptoJS.lib.WordArray.random(IV_LENGTH);

        // 키를 32바이트로 맞춤 (AES-256)
        const key = CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY.padEnd(32, '0').substring(0, 32));

        // AES 암호화
        const encrypted = CryptoJS.AES.encrypt(plainText, key, {
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7
        });

        // IV + 암호문을 합쳐서 Base64로 반환
        const combined = iv.concat(encrypted.ciphertext);
        return CryptoJS.enc.Base64.stringify(combined);
    } catch (error) {
        console.error('Encryption error:', error);
        return '';
    }
};

/**
 * AES256 복호화
 * @param encryptedText 암호화된 문자열 (Base64)
 * @returns 복호화된 평문
 */
export const decrypt = (encryptedText: string): string => {
    if (!encryptedText) return '';

    try {
        // Base64 디코딩
        const combined = CryptoJS.enc.Base64.parse(encryptedText);

        // IV와 암호문 분리
        const iv = CryptoJS.lib.WordArray.create(combined.words.slice(0, IV_LENGTH / 4), IV_LENGTH);
        const ciphertext = CryptoJS.lib.WordArray.create(
            combined.words.slice(IV_LENGTH / 4),
            combined.sigBytes - IV_LENGTH
        );

        // 키를 32바이트로 맞춤
        const key = CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY.padEnd(32, '0').substring(0, 32));

        // AES 복호화
        const decrypted = CryptoJS.AES.decrypt(
            { ciphertext: ciphertext } as CryptoJS.lib.CipherParams,
            key,
            {
                iv: iv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
            }
        );

        const result = decrypted.toString(CryptoJS.enc.Utf8);
        if (!result && encryptedText) {
            // 키 불일치로 복호화 결과가 빈 문자열 → 손상된 데이터 정리
            console.warn('Decryption produced empty result, clearing corrupted storage');
            sessionStorage.removeItem('dokocrew_user_info');
        }
        return result;
    } catch (error) {
        console.error('Decryption error:', error);
        // 키 불일치 등으로 복호화 실패 → 손상된 저장소 정리
        sessionStorage.removeItem('dokocrew_user_info');
        return '';
    }
};

/**
 * 민감정보 여부 확인
 */
export const isSensitiveField = (fieldName: string): boolean => {
    const sensitiveFields = [
        'userNo',
        'email',
        'phoneNumber',
        'phone',
        'mobile',
        'birthDate',
        'birth',
        'address',
        'realName',
        'name'
    ];
    return sensitiveFields.includes(fieldName);
};
